/**
 * Stage 2 of the bulk-add flow — a drag-and-drop review pane that lets the
 * user verify which project each task lands in, resolve unrecognised
 * project names (NEW or fuzzy-suggestion), and reorganise before commit.
 *
 * Submit stays disabled until every section's project is "decided"
 * (existing, user-mapped, or user-confirmed-new) so a confused user can't
 * accidentally spawn typo projects.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Calendar, Clock, Flag, FolderPlus, Lightbulb, Loader2, Trash2, Zap } from 'lucide-react';
import type { Project } from '@trackmind/core';
import { formatDuration } from '@trackmind/core';
import type { BulkSection, ProjectResolution } from '@/utils/bulkTaskParser';
import { allResolutionsDecided } from '@/utils/bulkTaskParser';
import { cn } from '@/lib/utils';
import { ProjectPicker } from '@/components/ProjectPicker'

interface BulkAddReviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSections: BulkSection[];
  projects: Project[];
  defaultProjectId: string;
  onSubmit: (sections: BulkSection[]) => Promise<void>;
}

const PRIORITY_LABEL: Record<number, string> = { 1: 'V.Low', 2: 'Low', 3: 'Med', 4: 'High', 5: 'Crit' };

function fmtDeadline(d?: Date): string | null {
  if (!d) return null;
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtMinutes(m?: number): string | null {
  return m ? formatDuration(m) : null;
}

// ─── Drag payload ───────────────────────────────────────────────────────────

interface DragPayload {
  sourceSectionId: string;
  taskId: string;
}

const DRAG_MIME = 'application/x-trackmind-bulk-task';

// ─── Component ──────────────────────────────────────────────────────────────

export function BulkAddReview({
  open,
  onOpenChange,
  initialSections,
  projects,
  defaultProjectId,
  onSubmit,
}: BulkAddReviewProps) {
  // The parent re-mounts this dialog on every open, so initialising state
  // from props here is correct — we always start from the latest parse.
  const [sections, setSections] = useState<BulkSection[]>(initialSections);
  const [submitting, setSubmitting] = useState(false);
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);

  const projectById = useMemo(() => new Map(projects.map(p => [p.id, p])), [projects]);
  const activeProjects = useMemo(() => projects.filter(p => p.status !== 'archived'), [projects]);

  // ─── Section mutations ──────────────────────────────────────────────────

  const updateResolution = useCallback((sectionId: string, resolution: ProjectResolution) => {
    setSections(prev => prev.map(s => (s.id === sectionId ? { ...s, resolution } : s)));
  }, []);

  const removeTask = useCallback((sectionId: string, taskId: string) => {
    setSections(prev =>
      prev
        .map(s => (s.id === sectionId ? { ...s, tasks: s.tasks.filter(t => t.id !== taskId) } : s))
        // Don't drop sections with explicit headers even if empty — user may
        // still want to create the (empty) project. Drop only the default
        // section when empty.
        .filter(s => s.tasks.length > 0 || s.rawHeader !== null)
    );
  }, []);

  const removeSection = useCallback((sectionId: string) => {
    setSections(prev => prev.filter(s => s.id !== sectionId));
  }, []);

  // Move a task between sections.
  const moveTask = useCallback((sourceSectionId: string, taskId: string, targetSectionId: string) => {
    if (sourceSectionId === targetSectionId) return;
    setSections(prev => {
      const src = prev.find(s => s.id === sourceSectionId);
      const task = src?.tasks.find(t => t.id === taskId);
      if (!task) return prev;
      return prev
        .map(s => {
          if (s.id === sourceSectionId) return { ...s, tasks: s.tasks.filter(t => t.id !== taskId) };
          if (s.id === targetSectionId) return { ...s, tasks: [...s.tasks, task] };
          return s;
        })
        .filter(s => s.tasks.length > 0 || s.rawHeader !== null);
    });
  }, []);

  // Move a task onto a project in the right rail — find or create a section
  // that resolves to that project, then move the task there.
  const moveTaskToProject = useCallback((sourceSectionId: string, taskId: string, projectId: string) => {
    setSections(prev => {
      const src = prev.find(s => s.id === sourceSectionId);
      const task = src?.tasks.find(t => t.id === taskId);
      if (!task) return prev;

      // Find an existing section that targets this project.
      const existingTarget = prev.find(s => {
        const r = s.resolution;
        if (r.kind === 'existing-exact' && r.projectId === projectId) return true;
        if (r.kind === 'user-mapped-existing' && r.projectId === projectId) return true;
        if (r.kind === 'default' && projectId === defaultProjectId) return true;
        return false;
      });

      if (existingTarget) {
        if (existingTarget.id === sourceSectionId) return prev;
        return prev
          .map(s => {
            if (s.id === sourceSectionId) return { ...s, tasks: s.tasks.filter(t => t.id !== taskId) };
            if (s.id === existingTarget.id) return { ...s, tasks: [...s.tasks, task] };
            return s;
          })
          .filter(s => s.tasks.length > 0 || s.rawHeader !== null);
      }

      // Otherwise spin up a new section for this project.
      const projectName = projectById.get(projectId)?.name ?? '';
      const newSection: BulkSection = {
        id: `s-rail-${projectId}-${Date.now()}`,
        rawHeader: projectName,
        resolution: { kind: 'existing-exact', projectId },
        tasks: [task],
      };
      return prev
        .map(s => (s.id === sourceSectionId ? { ...s, tasks: s.tasks.filter(t => t.id !== taskId) } : s))
        .filter(s => s.tasks.length > 0 || s.rawHeader !== null)
        .concat(newSection);
    });
  }, [defaultProjectId, projectById]);

  // ─── Drag handlers ──────────────────────────────────────────────────────

  const onChipDragStart = (e: React.DragEvent, sourceSectionId: string, taskId: string) => {
    const payload: DragPayload = { sourceSectionId, taskId };
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onTargetDragOver = (e: React.DragEvent, targetId: string) => {
    if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget(targetId);
  };

  const onTargetDragLeave = (targetId: string) => {
    setDragOverTarget(prev => (prev === targetId ? null : prev));
  };

  const onSectionDrop = (e: React.DragEvent, targetSectionId: string) => {
    e.preventDefault();
    setDragOverTarget(null);
    const raw = e.dataTransfer.getData(DRAG_MIME);
    if (!raw) return;
    const { sourceSectionId, taskId } = JSON.parse(raw) as DragPayload;
    moveTask(sourceSectionId, taskId, targetSectionId);
  };

  const onProjectDrop = (e: React.DragEvent, projectId: string) => {
    e.preventDefault();
    setDragOverTarget(null);
    const raw = e.dataTransfer.getData(DRAG_MIME);
    if (!raw) return;
    const { sourceSectionId, taskId } = JSON.parse(raw) as DragPayload;
    moveTaskToProject(sourceSectionId, taskId, projectId);
  };

  // ─── Submit ─────────────────────────────────────────────────────────────

  const decided = allResolutionsDecided(sections);
  const totalTasks = sections.reduce((n, s) => n + s.tasks.length, 0);
  const newProjectCount = sections.filter(s => s.resolution.kind === 'user-confirmed-new').length;
  const undecidedCount = sections.filter(
    s => s.resolution.kind === 'new' || s.resolution.kind === 'fuzzy-suggestion'
  ).length;

  const handleSubmit = async () => {
    if (!decided || submitting || totalTasks === 0) return;
    setSubmitting(true);
    try {
      await onSubmit(sections);
      onOpenChange(false);
    } catch (err) {
      console.error('[BulkAddReview] Submit failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render helpers ─────────────────────────────────────────────────────

  const renderResolutionUI = (section: BulkSection) => {
    const r = section.resolution;

    if (r.kind === 'default') {
      const def = projectById.get(defaultProjectId);
      return (
        <Badge variant="outline" className="text-xs">
          Default · {def?.name ?? 'Inbox'}
        </Badge>
      );
    }

    if (r.kind === 'existing-exact') {
      return <Badge variant="secondary" className="text-xs">✓ {projectById.get(r.projectId)?.name}</Badge>;
    }

    if (r.kind === 'user-mapped-existing') {
      return (
        <Badge variant="secondary" className="text-xs">
          → {projectById.get(r.projectId)?.name}
        </Badge>
      );
    }

    if (r.kind === 'user-confirmed-new') {
      return (
        <Badge variant="outline" className="text-xs border-blue-500 text-blue-600 dark:text-blue-400">
          <FolderPlus className="h-3 w-3 mr-1" /> New: {r.rawName}
        </Badge>
      );
    }

    if (r.kind === 'fuzzy-suggestion') {
      const suggested = projectById.get(r.suggestedProjectId);
      return (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge variant="outline" className="text-amber-600 border-amber-500">
            <Lightbulb className="h-3 w-3 mr-1" /> "{r.rawName}" — did you mean "{suggested?.name}"?
          </Badge>
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-xs"
            onClick={() =>
              updateResolution(section.id, {
                kind: 'user-mapped-existing',
                projectId: r.suggestedProjectId,
                rawName: r.rawName,
              })
            }
          >
            Yes, use it
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-xs"
            onClick={() => updateResolution(section.id, { kind: 'user-confirmed-new', rawName: r.rawName })}
          >
            No, create new
          </Button>
          <ProjectPicker
            projects={activeProjects}
            value={undefined}
            onChange={(projectId) =>
              updateResolution(section.id, { kind: 'user-mapped-existing', projectId, rawName: r.rawName })
            }
            statuses={['active', 'paused', 'completed']}
            placeholder="Map to…"
            aria-label="Map to an existing project"
            className="w-40"
            triggerClassName="h-7 text-xs px-2"
          />
        </div>
      );
    }

    // kind === 'new'
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge variant="outline" className="text-amber-600 border-amber-500">
          <AlertCircle className="h-3 w-3 mr-1" /> "{r.rawName}" not found
        </Badge>
        <Button
          size="sm"
          variant="outline"
          className="h-6 px-2 text-xs"
          onClick={() => updateResolution(section.id, { kind: 'user-confirmed-new', rawName: r.rawName })}
        >
          <FolderPlus className="h-3 w-3 mr-1" /> Create new
        </Button>
        <ProjectPicker
          projects={activeProjects}
          value={undefined}
          onChange={(projectId) =>
            updateResolution(section.id, { kind: 'user-mapped-existing', projectId, rawName: r.rawName })
          }
          statuses={['active', 'paused', 'completed']}
          placeholder="Map to existing…"
          aria-label="Map to an existing project"
          className="w-40"
          triggerClassName="h-7 text-xs px-2"
        />
      </div>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Review and create</DialogTitle>
          <p className="text-xs text-muted-foreground">
            Drag chips between sections, or onto a project on the right. Resolve any ⚠ items before submitting.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 overflow-y-auto pb-2 pr-1">
          {/* Left pane — sections */}
          <div className="md:col-span-2 space-y-3">
            {sections.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-8 border border-dashed rounded-md">
                Nothing to create yet — close this and add some tasks.
              </div>
            ) : sections.map(section => (
              <div
                key={section.id}
                onDragOver={(e) => onTargetDragOver(e, section.id)}
                onDragLeave={() => onTargetDragLeave(section.id)}
                onDrop={(e) => onSectionDrop(e, section.id)}
                className={cn(
                  'border rounded-md p-3 transition-colors',
                  dragOverTarget === section.id && 'border-primary bg-primary/5'
                )}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {section.rawHeader ?? 'Default project'}
                    </div>
                    {renderResolutionUI(section)}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeSection(section.id)}
                    title="Drop this section (tasks won't be created)"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {section.tasks.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic px-2 py-3 border border-dashed rounded">
                    Drop tasks here
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {section.tasks.map(task => (
                      <div
                        key={task.id}
                        draggable
                        onDragStart={(e) => onChipDragStart(e, section.id, task.id)}
                        className="flex items-center gap-2 px-2 py-1.5 bg-muted/40 rounded text-sm cursor-move hover:bg-muted"
                      >
                        <span className="flex-1 truncate">{task.parsed.title || task.rawLine}</span>
                        {task.parsed.deadline && (
                          <span className="inline-flex items-center gap-0.5 text-xs text-blue-600 dark:text-blue-400">
                            <Calendar className="h-3 w-3" />{fmtDeadline(task.parsed.deadline)}
                          </span>
                        )}
                        {task.parsed.priority && (
                          <span className="inline-flex items-center gap-0.5 text-xs text-amber-600 dark:text-amber-400">
                            <Flag className="h-3 w-3" />{PRIORITY_LABEL[task.parsed.priority]}
                          </span>
                        )}
                        {task.parsed.estimatedMinutes && (
                          <span className="inline-flex items-center gap-0.5 text-xs text-green-600 dark:text-green-400">
                            <Clock className="h-3 w-3" />{fmtMinutes(task.parsed.estimatedMinutes)}
                          </span>
                        )}
                        {task.parsed.energyLevel && (
                          <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
                            <Zap className="h-3 w-3" />{task.parsed.energyLevel}
                          </span>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 opacity-50 hover:opacity-100"
                          onClick={() => removeTask(section.id, task.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right rail — projects */}
          <div className="md:col-span-1 space-y-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold px-1">
              Drag onto a project
            </div>
            {activeProjects.map(p => (
              <div
                key={p.id}
                onDragOver={(e) => onTargetDragOver(e, `proj-${p.id}`)}
                onDragLeave={() => onTargetDragLeave(`proj-${p.id}`)}
                onDrop={(e) => onProjectDrop(e, p.id)}
                className={cn(
                  'border rounded-md px-3 py-2 text-sm transition-colors flex items-center justify-between gap-2',
                  dragOverTarget === `proj-${p.id}` && 'border-primary bg-primary/10'
                )}
              >
                <span className="truncate">{p.name}</span>
                {p.id === defaultProjectId && (
                  <Badge variant="outline" className="text-[10px] py-0 px-1">default</Badge>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="flex-row items-center justify-between gap-3 border-t pt-3">
          <div className="text-xs text-muted-foreground">
            {totalTasks} task{totalTasks === 1 ? '' : 's'}
            {newProjectCount > 0 && ` · ${newProjectCount} new project${newProjectCount === 1 ? '' : 's'}`}
            {undecidedCount > 0 && (
              <span className="text-amber-600 dark:text-amber-400 ml-2">
                · {undecidedCount} unresolved
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!decided || submitting || totalTasks === 0}>
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</>
              ) : (
                `Create ${totalTasks} task${totalTasks === 1 ? '' : 's'}${
                  newProjectCount > 0 ? ` + ${newProjectCount} project${newProjectCount === 1 ? '' : 's'}` : ''
                }`
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkAddReview;
